import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

import { DATE_FORMAT } from '../../utils/dateFormat';

import style from './style.module.css';

const DEFAULT_PLACEHOLDER = 'Select date range';

const toNativeDate = date => (date ? date.toDate() : undefined);

const toDayjsDate = date => (date ? dayjs(date) : undefined);

const formatRangeLabel = (dateFrom, dateTo, format) => {
  if (!dateFrom || !dateTo) {
    return '';
  }

  return `${dateFrom.format(format)} – ${dateTo.format(format)}`;
};

const DateRangePickerComponent = ({
  dateFrom,
  dateTo,
  onChange,
  format = DATE_FORMAT,
  placeholder = DEFAULT_PLACEHOLDER,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hoveredDate, setHoveredDate] = useState(undefined);
  const wrapperRef = useRef(null);

  const handleClickOutside = useCallback(event => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsCalendarOpen(false);
      setHoveredDate(undefined);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleToggleCalendar = useCallback(() => {
    setIsCalendarOpen(previousIsCalendarOpen => !previousIsCalendarOpen);
    setHoveredDate(undefined);
  }, []);

  const handleSelectRange = useCallback(
    (range, triggerDate) => {
      const hadCompleteRange = Boolean(dateFrom && dateTo);
      const nextRange = hadCompleteRange ? { from: triggerDate, to: undefined } : range;

      const nextDateFrom = toDayjsDate(nextRange?.from);
      const nextDateTo = toDayjsDate(nextRange?.to);

      onChange({ dateFrom: nextDateFrom, dateTo: nextDateTo });

      if (nextDateFrom && nextDateTo) {
        setIsCalendarOpen(false);
        setHoveredDate(undefined);
      }
    },
    [dateFrom, dateTo, onChange],
  );

  const handleDayMouseEnter = useCallback(day => {
    setHoveredDate(day);
  }, []);

  const handleDayMouseLeave = useCallback(() => {
    setHoveredDate(undefined);
  }, []);

  const selectedRange = {
    from: toNativeDate(dateFrom),
    to: toNativeDate(dateTo),
  };

  const previewRange = useMemo(() => {
    if (!hoveredDate || !selectedRange.from || selectedRange.to) {
      return false;
    }

    return hoveredDate < selectedRange.from
      ? { from: hoveredDate, to: selectedRange.from }
      : { from: selectedRange.from, to: hoveredDate };
  }, [hoveredDate, selectedRange.from, selectedRange.to]);

  const rangeLabel = formatRangeLabel(dateFrom, dateTo, format);

  const inputClassName = isCalendarOpen ? `${style.input} ${style.inputOpen}` : style.input;
  const valueClassName = rangeLabel ? style.value : `${style.value} ${style.placeholder}`;

  return (
    <div ref={wrapperRef} className={style.host}>
      <button className={inputClassName} onClick={handleToggleCalendar} type="button">
        <i aria-hidden="true" className={classNames('fa fa-calendar', style.icon)} />
        <span className={valueClassName}>{rangeLabel || placeholder}</span>
      </button>
      {isCalendarOpen && (
        <div className={style.panel}>
          <DayPicker
            defaultMonth={toNativeDate(dateFrom) || new Date()}
            min={1}
            mode="range"
            modifiers={{ rangeHover: previewRange }}
            modifiersClassNames={{ rangeHover: style.rangeHover }}
            navLayout="around"
            numberOfMonths={2}
            onDayMouseEnter={handleDayMouseEnter}
            onDayMouseLeave={handleDayMouseLeave}
            onSelect={handleSelectRange}
            selected={selectedRange}
          />
        </div>
      )}
    </div>
  );
};

DateRangePickerComponent.propTypes = {
  dateFrom: PropTypes.instanceOf(dayjs),
  dateTo: PropTypes.instanceOf(dayjs),
  format: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export const DateRangePicker = memo(DateRangePickerComponent);
